import React from "react";
import { Controller } from "react-hook-form";
import { Button, Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useLocation } from "react-router-dom";
import { useBasketState } from "./hooks/use-basket-state";
import { BasketItem } from "./components/basket-item";
import { formatPrice } from "../../helpers/formatPrice";
const SUPPORT_EMAIL = process.env.CONTACT_SUPPORT_EMAIL_ADDRESS ?? "";
export function Basket(props) {
    const { onSubmit, checkout } = props;
    const { checkoutLoading, secretKey, canPlaceOrder = true, showAccountNotLinked = false, } = checkout ?? { checkoutLoading: false, secretKey: "" };
    const { proceedToCheckout, handleApplyVoucher, handleClearBasketClicked, handleRemoveItemFromBasket, handleSubmit, hasItems, submitForm, enableApplyVoucher, basketId, items, itemCount, shipping, subtotal, total, outstandingAmount, voucherControl, loadingApplyVoucher, voucherDiscountTotal, } = useBasketState();
    const { pathname } = useLocation();
    const isCheckoutPage = pathname.includes("/checkout");
    const onCheckout = () => {
        onSubmit({ basket_id: basketId });
    };
    return (React.createElement("div", { className: "w-full rounded-xl px-4 py-8 bg-light-blue" },
        React.createElement("div", { className: "text-xl font-bold pb-4" }, "Basket"),
        !!itemCount && (React.createElement("div", { className: "flex justify-between py-2" },
            React.createElement("div", { className: "text-lg font-bold my-auto" }, `Items (${itemCount})`),
            React.createElement("button", { className: `rounded-full bg-black p-2 ease-in-out duration-200 hover:opacity-60`, onClick: handleClearBasketClicked },
                React.createElement(Icon, { className: "text-white m-auto", icon: "lucide:x", width: "20", height: "20" })))),
        React.createElement("div", { className: "max-h-[256px] overflow-y-auto" }, items?.map((item) => (React.createElement(BasketItem, { key: item?._id, item: item, onRemove: handleRemoveItemFromBasket })))),
        !!itemCount ? React.createElement("div", { className: "text-lg font-bold my-auto pb-4 pt-2" }, "Order Summary") : React.createElement("hr", { className: "w-full py-2" }),
        React.createElement("div", { className: "flex justify-between" },
            React.createElement("div", { className: "text-dark-gray" }, "Subtotal"),
            React.createElement("div", { className: "my-auto text-end" }, `${formatPrice({
                value: subtotal,
                decimalPlaces: 2,
            })}`)),
        React.createElement("div", { className: "flex justify-between" },
            React.createElement("div", { className: "text-dark-gray" }, "Shipping"),
            React.createElement("div", { className: "my-auto text-end" }, `${formatPrice({
                value: shipping,
                decimalPlaces: 2,
            })}`)),
        voucherDiscountTotal > 0 && (React.createElement("div", { className: "flex justify-between" },
            React.createElement("div", { className: "text-dark-gray" }, "Voucher Discount"),
            React.createElement("div", { className: "my-auto text-end text-red-500" }, `- ${formatPrice({
                value: voucherDiscountTotal.toFixed(2),
                decimalPlaces: 2,
            })}`))),
        React.createElement("div", { className: "flex justify-between py-2" },
            React.createElement("div", { className: "text-dark-gray" }, "Total"),
            React.createElement("div", { className: "flex flex-row items-center gap-4" },
                voucherDiscountTotal > 0 && (React.createElement("div", { className: "my-auto text-end text-sm line-through decoration-1" }, `${formatPrice({
                    value: (parseFloat(outstandingAmount?.toString() ?? "0") + voucherDiscountTotal).toFixed(2),
                    decimalPlaces: 2,
                })}`)),
                React.createElement("div", { className: "font-bold my-auto text-end" }, `${formatPrice({
                    value: outstandingAmount,
                    decimalPlaces: 2,
                })}`))),
        React.createElement("div", { className: "w-full flex flex-col gap-2 py-2 pb-4" },
            isCheckoutPage && showAccountNotLinked && (React.createElement("div", { className: "rounded-md border border-red-500 bg-red-50 px-3 py-2 text-sm text-red-600" },
                "Your customer ID has not been linked. Please contact",
                " ",
                React.createElement("a", { href: `mailto:${SUPPORT_EMAIL}`, target: "_blank", rel: "noopener noreferrer", className: "underline font-semibold" }, "support"),
                " ",
                "before placing your order.")),
            React.createElement(Button, { color: "primary", size: "lg", fullWidth: true, radius: "md", isDisabled: !hasItems || (isCheckoutPage && !canPlaceOrder), isLoading: checkoutLoading, onPress: () => {
                    if (isCheckoutPage) {
                        onCheckout();
                    }
                    else {
                        proceedToCheckout();
                    }
                } }, isCheckoutPage ? "Place Order" : "Proceed to Checkout")),
        React.createElement("hr", { className: "w-full" }),
        React.createElement("div", { className: "flex flex-col gap-2 py-2" },
            React.createElement("form", { onSubmit: submitForm(handleApplyVoucher) },
                React.createElement("div", { className: "font-semibold text-sm py-2" }, "Enter any promotions or coupons:"),
                React.createElement("div", { className: "flex flex-row gap-2" },
                    React.createElement(Controller, { control: voucherControl, name: "voucher", render: ({ field: { onChange, onBlur, value } }) => (React.createElement("input", { className: "min-w-[128px] w-2/3 rounded-xl border-0 bg-neutral-gray px-4 py-2", placeholder: "Enter Code", value: value, onChange: onChange, onBlur: onBlur })) }),
                    React.createElement(Button, { className: "w-1/3", type: "submit", color: "primary", size: "md", radius: "md", isDisabled: !enableApplyVoucher || loadingApplyVoucher }, loadingApplyVoucher ? React.createElement(Spinner, { size: "sm", color: "white" }) : "Apply"))))));
}
